import { background, BackgroundProps } from '@lib/style/mixins/background';
import { border, BorderProps } from '@lib/style/mixins/border';
import { borderRadius, BorderRadiusProps } from '@lib/style/mixins/borderRadius';
import { color, ColorProps } from '@lib/style/mixins/color';
import { flex, FlexProps } from '@lib/style/mixins/flex';
import { height, HeightProps } from '@lib/style/mixins/height';
import { margin, MarginProps } from '@lib/style/mixins/margin';
import { MaxAndMinProps, maxHeight, maxWidth, minHeight, minWidth } from '@lib/style/mixins/maxAndMin';
import { opacity, OpacityProps } from '@lib/style/mixins/opacity';
import { overflow, OverflowProps } from '@lib/style/mixins/overflow';
import { padding, PaddingProps } from '@lib/style/mixins/padding';
import { width, WidthProps } from '@lib/style/mixins/width';
import { classList } from '@lib/utils';
import { createMemo } from '@packages/react';
import { applyRef } from '@packages/react/applyRef';
import { kebabCase } from 'lodash';
import React, { ComponentType, CSSProperties, forwardRef, HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

export interface BlockProps
    extends BackgroundProps,
        PaddingProps,
        MarginProps,
        WidthProps,
        HeightProps,
        BorderRadiusProps,
        ColorProps,
        OverflowProps,
        OpacityProps,
        MaxAndMinProps,
        BorderProps,
        Omit<HTMLAttributes<HTMLElement>, 'color'> {
    display: CSSProperties['display'];
    children: any;
    component: any;
}

const createBlock = (display: null | CSSProperties['display']): any => css`
    && {
        position: relative;
        ${display ? `display: ${display};` : ''}

        ${overflow()};
        ${color()};
        ${opacity()};
        ${background()};
        ${borderRadius()};
        ${border()};
        ${margin()};
        ${padding()};
        ${maxWidth()};
        ${minWidth()};
        ${minHeight(1)};
        ${maxHeight()};
        ${width()};
        ${height()};
        ${display && /flex/.test(display) ? flex() : ''};
    }
`;

type BlockType<T extends Partial<BlockProps>> = ComponentType<T | { [key: string]: any }>;

function createBlockComponent<T extends Partial<BlockProps>>(
    name: string,
    displayValue: null | CSSProperties['display'],
): BlockType<T> {
    const MemoBlock = createMemo(
        forwardRef<any, T>((props, ref) => {
            const { children, display, className, component = 'div', ...rest } = props;

            const Component = component;

            overflow.clean(rest);
            color.clean(rest);
            opacity.clean(rest);
            background.clean(rest);
            borderRadius.clean(rest);
            border.clean(rest);
            margin.clean(rest);
            padding.clean(rest);
            maxWidth.clean(rest);
            minWidth.clean(rest);
            maxHeight.clean(rest);
            minHeight.clean(rest);
            width.clean(rest);
            height.clean(rest);
            flex.clean(rest);

            applyRef(rest, ref);

            const className$ = classList(className, `box__${kebabCase(name)}`);

            return (
                <Component className={className$} {...rest}>
                    {children}
                </Component>
            );
        }),
        {
            displayName: name,
        },
    );

    return styled(MemoBlock)<T>`
        ${createBlock(displayValue)}
    ` as any;
}

export const Flex: BlockType<Partial<FlexProps & BlockProps>> = createBlockComponent<Partial<FlexProps & BlockProps>>(
    'Flex',
    'flex',
);
export const InlineFlex: BlockType<Partial<FlexProps & BlockProps>> = createBlockComponent<
    Partial<FlexProps & BlockProps>
>('InlineFlex', 'inline-flex');
export const InlineBlock: BlockType<Partial<BlockProps>> = createBlockComponent<Partial<BlockProps>>(
    'InlineBlock',
    'inline-block',
);
export const Block: BlockType<Partial<BlockProps>> = createBlockComponent<Partial<BlockProps>>('Block', 'block');
export const None: BlockType<Partial<BlockProps>> = createBlockComponent<Partial<BlockProps>>('None', 'none');
export const Box: BlockType<Partial<BlockProps>> = createBlockComponent<Partial<BlockProps>>('Box', null);
